import { useState } from 'react';
import { ChevronDown, Plus, Trash2, X } from 'lucide-react';
import { ampelBadge, fmtDate, toDateInputValue } from './ampel';
import NotizFeld from './NotizFeld';
import type { TeamMember } from '../../lib/teamsApi';
import type { EigeneAufgabe, EigeneAufgabeItem } from '../../lib/eigeneAufgabenApi';

/**
 * Eine selbst angelegte Aufgabe — gleiche Karte wie eine Nachforderung.
 *
 * Aufklappen zeigt die eigenen Punkte, Frist, Notiz und den Erledigt-Knopf. Anders als bei
 * der Nachforderung gibt es hier keine Original-Mail (es gibt keine), dafür „Löschen": Was
 * man selbst anlegt, darf man auch wieder wegwerfen — erledigt ist etwas anderes als falsch
 * angelegt.
 */
export default function EigeneAufgabeEntry({
  a,
  mitglieder,
  onToggleItem,
  onPunktHinzu,
  onPunktWeg,
  onFrist,
  onNotiz,
  onErledigt,
  onLoeschen,
  onZustaendig,
}: {
  a: EigeneAufgabe;
  mitglieder: TeamMember[];
  onToggleItem: (a: EigeneAufgabe, it: EigeneAufgabeItem) => void;
  onPunktHinzu: (a: EigeneAufgabe, label: string) => void;
  onPunktWeg: (a: EigeneAufgabe, itemId: string) => void;
  onFrist: (a: EigeneAufgabe, value: string | null) => void;
  onNotiz: (a: EigeneAufgabe, value: string) => void;
  onErledigt: (a: EigeneAufgabe) => void;
  onLoeschen: (a: EigeneAufgabe) => void;
  onZustaendig: (a: EigeneAufgabe, userId: number | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const [neuerPunkt, setNeuerPunkt] = useState('');
  const badge = a.frist ? ampelBadge(a.frist) : null;
  const alleErledigt = a.items.length > 0 && a.items.every((it) => it.done);

  const punktHinzu = () => {
    const t = neuerPunkt.trim();
    if (!t) return;
    onPunktHinzu(a, t);
    setNeuerPunkt('');
  };

  return (
    <div
      data-testid="eigene-aufgabe"
      data-vollstaendig={alleErledigt ? 'ja' : 'nein'}
      className={`rounded-lg border hover:shadow-sm transition overflow-hidden ${
        alleErledigt
          ? 'border-green-300 bg-green-50/60 dark:border-green-800 dark:bg-green-900/10'
          : 'border-slate-200 dark:border-slate-700'
      }`}
    >
      <div
        role="button"
        tabIndex={0}
        onClick={() => setOpen((v) => !v)}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpen((v) => !v); } }}
        className="w-full flex items-start gap-2 px-3 py-2.5 text-left cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700/40 transition-colors"
      >
        <ChevronDown className={`w-3.5 h-3.5 text-slate-400 shrink-0 mt-0.5 transition-transform ${open ? '' : '-rotate-90'}`} />
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-1.5 min-w-0">
            <span className="font-medium text-sm text-slate-900 dark:text-white truncate">
              {a.kundeName || a.vorgangsnummer || <span className="text-slate-400 italic font-normal">Ohne Kunde</span>}
            </span>
            {a.kundeName && a.vorgangsnummer && (
              <span className="text-xs text-slate-400 dark:text-slate-500 shrink-0">{a.vorgangsnummer}</span>
            )}
          </div>
          <div className="text-sm text-slate-600 dark:text-slate-300 truncate mt-0.5">{a.titel}</div>
        </div>
        <div className="ml-auto flex flex-col items-end gap-1 shrink-0">
          <div className="flex items-center gap-2">
            {a.zustaendigName && (
              <span className="text-[12px] font-medium px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-200 whitespace-nowrap">
                {a.zustaendigName}
              </span>
            )}
            {alleErledigt && (
              <span className="text-[12px] font-medium px-1.5 py-0.5 rounded-full bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300 whitespace-nowrap">
                ✓ Vollständig
              </span>
            )}
            {/* Ohne Frist keine Plakette: Eine selbst gesetzte Aufgabe muss keine haben —
                ein gelbes „Frist prüfen" wäre hier eine erfundene Mahnung. */}
            {!alleErledigt && badge && (
              <span className={`text-[12px] px-1.5 py-0.5 rounded-full font-medium whitespace-nowrap ${badge.cls}`}>
                {badge.emoji} {badge.text}
              </span>
            )}
          </div>
          {fmtDate(a.updatedAt) && (
            <span title="zuletzt bearbeitet" className="text-[12px] text-slate-400 dark:text-slate-500 whitespace-nowrap">
              bearb. {fmtDate(a.updatedAt)}
            </span>
          )}
        </div>
      </div>

      {open && (
        <div className="px-3 pb-4 pt-2 space-y-3 border-t border-slate-100 dark:border-slate-700/50">
          <ul className="space-y-1.5">
            {a.items.map((it) => (
              <li key={it.id} className="flex items-start gap-2 text-sm group">
                <input
                  type="checkbox"
                  aria-label={it.label}
                  checked={it.done}
                  onChange={() => onToggleItem(a, it)}
                  className="mt-0.5 w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500 cursor-pointer"
                />
                <span className={`flex-1 ${it.done ? 'line-through text-slate-400 dark:text-slate-500' : 'text-slate-800 dark:text-slate-200'}`}>
                  {it.label}
                </span>
                <button
                  onClick={() => onPunktWeg(a, it.id)}
                  aria-label={`Punkt entfernen: ${it.label}`}
                  className="p-0.5 text-slate-300 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </li>
            ))}
          </ul>

          <div className="flex gap-1.5">
            <input
              type="text"
              aria-label="Punkt hinzufügen"
              value={neuerPunkt}
              onChange={(e) => setNeuerPunkt(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); punktHinzu(); } }}
              placeholder="Punkt hinzufügen"
              className="flex-1 px-2.5 py-1 border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white rounded-md text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 placeholder:text-slate-400"
            />
            <button
              onClick={punktHinzu}
              aria-label="Punkt übernehmen"
              className="px-2 rounded-md border border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700"
            >
              <Plus className="w-3.5 h-3.5" />
            </button>
          </div>

          <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
            <label className="flex items-center gap-2 text-xs text-slate-600 dark:text-slate-400">
              Frist
              <input
                type="date"
                aria-label="Frist"
                value={toDateInputValue(a.frist)}
                onClick={(e) => e.stopPropagation()}
                onChange={(e) => onFrist(a, e.target.value || null)}
                className="px-2 py-1 border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white rounded-md text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </label>
            <label className="flex items-center gap-2 text-xs text-slate-600 dark:text-slate-400">
              Zuständig
              <select
                aria-label="Zuständig"
                value={a.zustaendigUserId != null ? String(a.zustaendigUserId) : ''}
                onClick={(e) => e.stopPropagation()}
                onChange={(e) => onZustaendig(a, e.target.value ? Number(e.target.value) : null)}
                className="px-2 py-1 border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white rounded-md text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Niemand</option>
                {mitglieder.map((m) => (
                  <option key={m.id} value={m.id}>{m.contactPerson?.trim() || m.email}</option>
                ))}
              </select>
            </label>
          </div>

          <NotizFeld
            wert={a.notiz}
            placeholder="Notiz zu dieser Aufgabe…"
            onSpeichern={(v) => onNotiz(a, v)}
          />

          <div className="flex items-center justify-between">
            <button
              onClick={() => onLoeschen(a)}
              className="inline-flex items-center gap-1.5 px-2 py-1 text-xs font-medium rounded-md text-slate-500 hover:text-red-600 dark:text-slate-400 dark:hover:text-red-400"
            >
              <Trash2 className="w-3.5 h-3.5" /> Löschen
            </button>
            <button
              onClick={() => onErledigt(a)}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-md bg-green-600 hover:bg-green-700 text-white"
            >
              Erledigt
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
